import { IState } from "@/apps/credit-line/models/IState";
import {
  DeviceCohort,
  ICardInfo,
  IShipmentInfo,
  ITransaction,
  IWithdrawal,
} from "@/apps/credit-line/models/CreditLineDetails";
import { IOffer } from "@/apps/credit-line/models/IOffer";
import TransactionFilter from "@/apps/credit-line/types/TransactionFilter";
import { CardType } from "@/apps/credit-line/types/cardType";
import { PayJoyCardStatus } from "@/apps/credit-line/models/PayJoyCardStatus";
import { IVuexState } from "@/shared-models/IVuex";
import { GetterTree } from "vuex";
import { ReportLostOrDamagedCard } from "../types/cards";
import { RequireAmlSurveyResponse } from "@/apps/credit-line/models/IAmlSurvey";
import { WithdrawMethod } from "@/apps/credit-line/types/withdrawMethod";
import { AreValidationsRequiredResponse } from "@/apps/credit-line/models/IKycValidations";
import {
  IWithdrawalEstimate,
  IWithdrawalEstimateField,
} from "@/apps/credit-line/models/IWithdrawalEstimate";

const getters: GetterTree<IState, IVuexState> = {
  getPendingWithdraw: (state: IState): IWithdrawal | null => {
    return state.pendingWithdraw ?? null;
  },
  getWithdrawals: (state: IState): IWithdrawal[] | null => {
    return state.withdrawals ?? null;
  },
  getCurrency: (state: IState): string => {
    return state.currency ?? "MXN";
  },
  getSelectedWithdrawMethod: (state: IState): WithdrawMethod | null => {
    return state.selectedWithdrawMethod;
  },
  getWithdrawalEstimate: (state: IState): IWithdrawalEstimate | null => {
    return state.withdrawalEstimate ?? null;
  },
  getWithdrawalEstimateFields: (
    state: IState
  ): IWithdrawalEstimateField[] | null => {
    return state.withdrawalEstimateFields ?? null;
  },
  getOffer: (state: IState): IOffer | null => {
    return state.offer ?? null;
  },
  getOfferUnavailable: (state: IState): boolean | null => {
    return state.offer_unavailable ?? null;
  },
  getTransactionsFilters: (state: IState): TransactionFilter[] | null => {
    return state.transactionsFilters ?? null;
  },
  getTransactionList: (state: IState): ITransaction[] | null => {
    return state.transactionList ?? null;
  },
  // Credit Card
  getCardsByType:
    (state: IState): ((type: CardType) => Array<ICardInfo>) =>
    (type: CardType) => {
      return (state.summary?.cards ?? []).filter((card) => card.type === type);
    },
  getCardByLastFour:
    (state: IState): ((lastFour: string) => ICardInfo | undefined) =>
    (lastFour: string) => {
      return (state.summary?.cards ?? []).find(
        (card) => card.lastFour === lastFour
      );
    },
  getActivePhysicalCard: (state: IState): ICardInfo | undefined => {
    return (state.summary?.cards ?? [])
      .filter((card) => card.status === "ACTIVE")
      .find((card) => card.type === "PHYSICAL");
  },
  getHasEverHadCardOfType:
    (state: IState): ((type: CardType) => boolean) =>
    (type) => {
      return (
        (state.summary?.cards ?? []).find((card) => card.type === type) !==
        undefined
      );
    },
  getIsCreatingVirtualCard: (state: IState): boolean => {
    return state.runningActions.createVirtualCreditCard;
  },
  getShipmentByStatus:
    (
      state: IState
    ): ((status: PayJoyCardStatus) => IShipmentInfo | undefined) =>
    (status) => {
      return (state.summary?.shipments ?? []).find(
        (shipment) => shipment.status === status
      );
    },
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getDeviceCohort: (state: IState, getters): DeviceCohort => {
    if (!state.summary) {
      return null;
    }

    if (getters.getActivePhysicalCard) {
      if (!getters.getHasEverHadCardOfType("VIRTUAL")) {
        return "CC_ACTIVATED";
      }
    } else {
      if (getters.getHasEverHadCardOfType("PHYSICAL")) {
        if (!getters.getHasEverHadCardOfType("VIRTUAL")) {
          return "CC_EXISTED_NOT_ACTIVE";
        }
      }

      if ((state.summary?.shipments ?? []).length > 0) {
        if (getters.getShipmentByStatus("DELIVERED")) {
          return "CC_RECEIVED_NOT_ACTIVATED";
        }

        return "CC_REQUESTED_NOT_RECEIVED";
      } else {
        if (!getters.getHasEverHadCardOfType("VIRTUAL")) {
          return "CC_NEVER_REQUESTED";
        }
      }
    }

    return null;
  },
  getShipmentLogs: (
    state: IState
  ): Array<{
    date: string;
    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    message: Record<string, any>;
  }> | null => {
    return state.shipmentLogs ?? null;
  },
  getReportLostOrDamagedCard: (
    state: IState
  ): ReportLostOrDamagedCard | null => {
    return state.reportLostOrDamagedCard ?? null;
  },
  getWithdrawalValidationResponse: (
    state: IState
  ): AreValidationsRequiredResponse | null => {
    return state.withdrawalValidation ?? null;
  },
  getAmlSurveyData: (state: IState): RequireAmlSurveyResponse | null => {
    return state.amlSurveyData;
  },
  getCurpValidationResult: (state: IState) => state.curpValidationResult,
  wasValidationSuccessful: (state: IState) => state.validationSuccess,
};

export default getters;
